/* Pengelly Garden Centre — Wishlist
   Stores wishlist in localStorage as an array of product objects:
   { id, title, url, image, price }
*/

const WISHLIST_KEY = 'pgc_wishlist';

const Wishlist = {
  get() {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || [];
    } catch (e) {
      return [];
    }
  },

  save(items) {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items } }));
  },

  has(id) {
    return this.get().some(p => String(p.id) === String(id));
  },

  add(product) {
    const items = this.get();
    if (!this.has(product.id)) {
      items.push(product);
      this.save(items);
    }
  },

  remove(id) {
    const items = this.get().filter(p => String(p.id) !== String(id));
    this.save(items);
  },

  toggle(product) {
    if (this.has(product.id)) {
      this.remove(product.id);
      return false;
    } else {
      this.add(product);
      return true;
    }
  },

  count() {
    return this.get().length;
  }
};

/* ── Wishlist Button Web Component ── */
class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.productId   = this.dataset.productId;
    this.productTitle = this.dataset.productTitle || '';
    this.productUrl  = this.dataset.productUrl || '';
    this.productImage = this.dataset.productImage || '';
    this.productPrice = this.dataset.productPrice || '';

    this.render();

    // Use capture phase so we intercept before the Dawn overlay anchor gets it
    this.addEventListener('click', this.handleClick.bind(this), true);
    this.addEventListener('mousedown', function(e){ e.stopPropagation(); }, true);
    this.addEventListener('touchstart', function(e){ e.stopPropagation(); }, { capture: true, passive: true });
    document.addEventListener('wishlist:updated', () => this.render());
  }

  render() {
    const active = Wishlist.has(this.productId);
    this.innerHTML = `
      <button
        class="wishlist-btn${active ? ' wishlist-btn--active' : ''}"
        aria-label="${active ? 'Remove from wishlist' : 'Add to wishlist'}"
        title="${active ? 'Remove from wishlist' : 'Add to wishlist'}"
        type="button"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="22" height="22" aria-hidden="true">
          <!-- Pot body -->
          <path d="M22 45 L28 80 Q28 84 32 84 L68 84 Q72 84 72 80 L78 45 Z" stroke-width="4" stroke-linejoin="round"/>
          <!-- Pot rim -->
          <rect x="18" y="38" width="64" height="9" rx="4" stroke-width="4"/>
          <!-- Stem -->
          <line x1="50" y1="38" x2="50" y2="18" stroke-width="4" stroke-linecap="round"/>
          <!-- Left leaf -->
          <path d="M50 28 Q38 20 34 10 Q44 10 50 22" stroke-width="3" stroke-linejoin="round"/>
          <!-- Right leaf -->
          <path d="M50 20 Q62 12 66 2 Q56 4 50 16" stroke-width="3" stroke-linejoin="round"/>
        </svg>
      </button>`;
  }

  handleClick(e) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    const product = {
      id:    this.productId,
      title: this.productTitle,
      url:   this.productUrl,
      image: this.productImage,
      price: this.productPrice
    };
    const added = Wishlist.toggle(product);
    // Brief animation
    const btn = this.querySelector('.wishlist-btn');
    btn.classList.add('wishlist-btn--pop');
    setTimeout(() => btn.classList.remove('wishlist-btn--pop'), 300);
  }
}

customElements.define('wishlist-button', WishlistButton);

/* ── Global capture interceptor ──
   Dawn's full-unstyled-link anchor covers the whole card.
   We intercept at the document level (capture phase, before any element)
   and cancel navigation if the click path passes through a wishlist-button.
*/
document.addEventListener('click', function(e) {
  // Walk up the composed path to find if a wishlist-button is in the chain
  var path = e.composedPath ? e.composedPath() : [];
  for (var i = 0; i < path.length; i++) {
    var node = path[i];
    if (node && node.tagName && node.tagName.toLowerCase() === 'wishlist-button') {
      e.preventDefault();
      e.stopImmediatePropagation();
      // Manually trigger the wishlist toggle since the component's own handler is blocked
      var wb = node;
      var product = {
        id:    wb.dataset.productId,
        title: wb.dataset.productTitle || '',
        url:   wb.dataset.productUrl || '',
        image: wb.dataset.productImage || '',
        price: wb.dataset.productPrice || ''
      };
      Wishlist.toggle(product);
      var btn = wb.querySelector('.wishlist-btn');
      if (btn) {
        btn.classList.add('wishlist-btn--pop');
        setTimeout(function(){ btn.classList.remove('wishlist-btn--pop'); }, 300);
      }
      // Re-render all wishlist buttons for this product
      document.querySelectorAll('wishlist-button[data-product-id="' + product.id + '"]').forEach(function(el) {
        if (el.render) el.render();
      });
      return;
    }
    // Stop walking if we hit an anchor that is NOT inside a wishlist-button
    if (node && node.tagName && node.tagName.toLowerCase() === 'a') break;
  }
}, true);

/* ── Wishlist Page Renderer ── */
class WishlistPage extends HTMLElement {
  connectedCallback() {
    this.render();
    document.addEventListener('wishlist:updated', () => this.render());
  }

  render() {
    const items = Wishlist.get();
    if (items.length === 0) {
      this.innerHTML = `
        <div class="wishlist-empty">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="80" height="80" aria-hidden="true">
            <path d="M22 45 L28 80 Q28 84 32 84 L68 84 Q72 84 72 80 L78 45 Z" stroke-width="4" stroke-linejoin="round"/>
            <rect x="18" y="38" width="64" height="9" rx="4" stroke-width="4"/>
            <line x1="50" y1="38" x2="50" y2="18" stroke-width="4" stroke-linecap="round"/>
            <path d="M50 28 Q38 20 34 10 Q44 10 50 22" stroke-width="3" stroke-linejoin="round"/>
            <path d="M50 20 Q62 12 66 2 Q56 4 50 16" stroke-width="3" stroke-linejoin="round"/>
          </svg>
          <h2>Your wishlist is empty</h2>
          <p>Browse our collections and tap the plant pot icon to save products you love.</p>
          <a href="/collections/all" class="button button--primary">Browse All Products</a>
        </div>`;
      return;
    }

    this.innerHTML = `
      <p class="wishlist-count">${items.length} item${items.length !== 1 ? 's' : ''} saved</p>
      <div class="wishlist-grid">
        ${items.map(p => `
          <div class="wishlist-card" data-id="${p.id}">
            <a href="${p.url}" class="wishlist-card__image-link">
              ${p.image
                ? `<img src="${p.image}" alt="${p.title}" loading="lazy" width="300" height="300">`
                : `<div class="wishlist-card__no-image"></div>`}
            </a>
            <div class="wishlist-card__info">
              <a href="${p.url}" class="wishlist-card__title">${p.title}</a>
              ${p.price ? `<p class="wishlist-card__price">${p.price}</p>` : ''}
              <div class="wishlist-card__actions">
                <a href="${p.url}" class="button button--secondary wishlist-card__view">View Product</a>
                <button class="wishlist-card__remove link" data-remove-id="${p.id}" type="button" aria-label="Remove ${p.title} from wishlist">
                  Remove
                </button>
              </div>
            </div>
          </div>`).join('')}
      </div>
      <div class="wishlist-page__footer">
        <button class="button button--tertiary wishlist-clear" type="button">Clear Wishlist</button>
      </div>`;

    this.querySelectorAll('[data-remove-id]').forEach(btn => {
      btn.addEventListener('click', () => {
        Wishlist.remove(btn.dataset.removeId);
      });
    });

    const clearBtn = this.querySelector('.wishlist-clear');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        if (confirm('Remove all items from your wishlist?')) {
          Wishlist.save([]);
        }
      });
    }
  }
}

customElements.define('wishlist-page', WishlistPage);

/* ── Wishlist Count Badge (for nav link) ── */
class WishlistCount extends HTMLElement {
  connectedCallback() {
    this.update();
    document.addEventListener('wishlist:updated', () => this.update());
  }
  update() {
    const count = Wishlist.count();
    this.textContent = count > 0 ? `(${count})` : '';
    this.style.display = count > 0 ? 'inline' : 'none';
    // Update PGC header wishlist badge
    const pgcBadge = document.getElementById('pgc-wishlist-count');
    if (pgcBadge) {
      pgcBadge.textContent = count;
      pgcBadge.style.display = count > 0 ? 'inline-flex' : 'none';
    }
    // Fill the header plant pot icon when wishlist has items
    const headerIcon = document.querySelector('.header__icon--wishlist');
    if (headerIcon) {
      if (count > 0) {
        headerIcon.classList.add('wishlist-has-items');
      } else {
        headerIcon.classList.remove('wishlist-has-items');
      }
    }
  }
}
customElements.define('wishlist-count', WishlistCount);

/* ── Move wishlist buttons out of the overlay area ──
   Dawn's card__inner contains a full-unstyled-link::after that covers the
   entire card image area. We relocate any wishlist-button that is inside
   card__media or card__inner to be a direct sibling of card__information
   (inside card__content but AFTER the information div), which is outside
   the overlay's reach.
*/
function relocateWishlistButtons() {
  document.querySelectorAll('.card__media wishlist-button, .card__inner wishlist-button').forEach(function(wb) {
    // Find the nearest card__content ancestor
    var cardContent = wb.closest('.card__content') || wb.closest('.card-wrapper');
    if (!cardContent) return;
    // Find card__information inside it
    var cardInfo = cardContent.querySelector('.card__information');
    if (cardInfo && cardInfo.parentElement) {
      // Insert wishlist button after card__information
      cardInfo.parentElement.insertBefore(wb, cardInfo.nextSibling);
      wb.style.position = 'relative';
      wb.style.zIndex = '10';
      wb.style.pointerEvents = 'all';
    }
  });
}

// Run on DOM ready and after any Shopify section re-renders
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', relocateWishlistButtons);
} else {
  relocateWishlistButtons();
}
// Also run after a short delay to catch lazy-loaded cards
setTimeout(relocateWishlistButtons, 500);
setTimeout(relocateWishlistButtons, 1500);
